import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Minus, Plus, Trash2, LockIcon, ShieldCheck } from 'lucide-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useAppStore, useCheckoutStore } from '../store/Store';
import CartItemSkeleton from '../components/CartItemSkeleton';

import axios from 'axios';
import { BASE_URL } from '../apis/constant';

// --- Fixed CartItem Component ---
const CartItem = ({ item, onUpdateQuantity, onRemove, }) => {

    const price = Number(item?.price) || 0;
    const offerPrice = item?.offerPrice ? Number(item.offerPrice) : price;
    const quantity = Number(item?.quantity) || 1;
    const totalPrice = offerPrice * quantity;
    const hasDiscount = offerPrice < price;

    return (
        <div className="flex gap-4 bg-white p-3 border border-gray-100 rounded-2xl shadow-sm transition-all duration-200">
            {/* Image - Fixed Size */}
            <div className="relative w-24 h-24 flex-shrink-0 overflow-hidden rounded-xl bg-gray-50 border border-gray-50 self-center">
                <img
                    src={item?.image}
                    alt={item?.name || 'Product'}
                    className="w-full h-full object-cover mix-blend-multiply"
                    onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTMwQzE2NS44MTIgMTMwIDEzOCAxNTcuODEyIDEzOCAxOTJDMTM4IDIyNi4xODggMTY1LjgxMiAyNTQgMjAwIDI1NEMyMzQuMTg4IDI1NCAyNjIgMjI2LjE4OCAyNjIgMTkyQzI2MiAxNTcuODEyIDIzNC4xODggMTMwIDIwMCAxMzBaTTI1NiAyODRIMTQ0QzEzMS4yNjQgMjg0IDEyMCAyOTUuMjY0IDEyMCAzMDhWMzQwQzEyMDM0Ni42MjggMTI1LjM3MiAzNTIgMTMyIDM1MkgyNjhDMjc0LjYyOCAzNTIgMjgwIDM0Ni42MjggMjgwIDM0MFYzMDhDMjgwIDI5NS4yNjQgMjY4LjczNiAyODQgMjU2IDI4NFoiIGZpbGw9IiNENERDREIiLz4KPC9zdmc+Cg==';
                    }}
                />
            </div>

            {/* Content */}
            <div className="flex flex-col flex-grow justify-between py-0.5 overflow-hidden">

                {/* Top Row: Name & Delete */}
                <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                        <h3 className="font-bold text-gray-900 text-sm truncate leading-tight mb-1">
                            {item?.name || 'Unnamed Product'}
                        </h3>
                        {/* Variant Text - FIX: Check if variant exists */}
                        {item?.variantType && item?.variantValue && (
                            <p className="text-xs text-gray-500 font-medium">
                                {item.variantType}: <span className="text-gray-700">{item.variantValue}</span>
                            </p>
                        )}
                    </div>

                    <button
                        onClick={() => onRemove(item?.id)}
                        className="text-gray-300 hover:text-red-500 transition-colors p-1 -mr-1"
                        aria-label="Remove item"
                    >
                        <Trash2 size={18} strokeWidth={2} />
                    </button>
                </div>

                {/* Bottom Row: Quantity & Price Layout */}
                <div className="flex justify-between items-end mt-2">

                    {/* Quantity Selector */}
                    <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 h-8 shadow-sm">
                        <button
                            onClick={() => onUpdateQuantity(item?.id, -1)}
                            disabled={quantity <= 1}
                            className="w-8 h-full flex items-center justify-center text-gray-500 hover:text-black disabled:opacity-30 active:bg-gray-200 rounded-l-lg transition-colors"
                        >
                            <Minus size={14} strokeWidth={2.5} />
                        </button>
                        <span className="w-6 text-center text-xs font-bold text-gray-900">
                            {quantity}
                        </span>
                        <button
                            onClick={() => onUpdateQuantity(item?.id, 1)}
                            disabled={quantity >= (item?.stock || 10)}
                            className="w-8 h-full flex items-center justify-center text-gray-500 hover:text-black disabled:opacity-30 active:bg-gray-200 rounded-r-lg transition-colors"
                        >
                            <Plus size={14} strokeWidth={2.5} />
                        </button>
                    </div>

                    {/* Price Representation - FIX: Safe number formatting */}
                    <div className="text-right flex flex-col items-end justify-end">
                        {/* Unit Calculation - Shows Offer Price x Quantity */}
                        <div className="text-[10px] font-medium text-gray-400 mb-0.5">
                            {quantity} x ৳{offerPrice?.toLocaleString() || '0'}
                            {hasDiscount && (
                                <span className="ml-1 line-through text-gray-300">
                                    ৳{price?.toLocaleString() || '0'}
                                </span>
                            )}
                        </div>
                        {/* Total Line Price */}
                        <p className="text-base font-extrabold text-gray-900 leading-none">
                            ৳{totalPrice?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Main Component ---
const Cart = () => {
    const {
        cartItems = [],
        cartCount = 0,
        updateCartItemQuantity,
        removeFromCart,
        initializeCart,
        cartApiLoading,
    } = useAppStore();

    const { setCheckoutItems } = useCheckoutStore();

    const navigate = useNavigate();

    // Initialize cart when component mounts
    useEffect(() => {
        initializeCart();
    }, [initializeCart]);

    const handleUpdateQuantity = (id, delta) => {
        const item = cartItems.find(item => item.id === id);
        if (item) {
            const currentQuantity = Number(item.quantity) || 1;
            const stock = Number(item.stock) || 10;
            const newQuantity = Math.max(1, Math.min(stock, currentQuantity + delta));
            updateCartItemQuantity(id, newQuantity);
        }
    };

    const RemoveFromCart = async (id) => {
        const customer_id = localStorage.getItem("customerId");
        if (!customer_id) return false;

        try {
            const { status } = await axios.post(`${BASE_URL}/api/remove_from_cart/`, {
                customer_id,
                cart_item_id: id
            });
            return status === 200;
        } catch (error) {
            console.error('Error:', error);
            return false;
        }
    };

    const handleRemoveItem = async (id) => {
        const success = await RemoveFromCart(id);
        if (success) removeFromCart(id);
    };



    function transformProducts(input) {

        return input.map((item, index) => {
            const imagePath = '/' + item.image.replace(/^https?:\/\/[^/]+\/?/, '');


            return {
                id: (index + 1).toString(),
                name: item.name,
                productId :item.productId,
                variantId:item.id,
                variant_type: item.variantType,
                variant: item.variantValue,
                price: item.offerPrice && item.offerPrice !== item.price ? item.offerPrice : item.price,
                originalPrice: item.price,
                quantity: item.quantity,
                image: imagePath
            };
        });
    }



    const handleCheckOut = () => {
        setCheckoutItems(transformProducts(cartItems));
        navigate(`/checkout`);
    }


    if (!cartItems || cartItems.length === 0) {
        return (
            <div className="w-full max-w-[450px] mx-auto flex flex-col items-center justify-center px-6 bg-gray-50" style={{ height: 'calc(100dvh - 110px)' }}>

                {/* Lottie Animation */}
                <div className="w-48 h-48 mb-4">
                    <DotLottieReact
                        src="https://lottie.host/6fa380fe-08d4-44f8-9d8c-cfc446905b3d/tYfL8dvHle.lottie"
                        loop
                        autoplay
                    />
                </div>

                {/* Empty State Text */}
                <h2 className="text-lg font-medium text-gray-500 mb-2 text-center">
                    Cart is Empty
                </h2>
                <p className="text-sm text-gray-400 mb-6 text-center">
                    Add some products to get started.
                </p>

                {/* Shop Now Button */}
                <button
                    className="bg-gray-900 hover:bg-black text-white font-medium py-1 px-6 rounded-lg flex items-center gap-2 transition-colors duration-200"
                    onClick={() => navigate(`/shop`)}
                >
                    Shop Now
                </button>
            </div>
        );
    }

    // Main Cart View
    return (
        <div className="w-full max-w-[450px] relative flex flex-col mx-auto pb-[60px]">

            {/* Header - Fixed 54px height */}
            <div className="h-[54px] flex-shrink-0 bg-white border-b border-gray-100 px-5 flex items-center justify-start border rounded-b-xl">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 -ml-2 text-gray-500 hover:text-black transition-colors rounded-full hover:bg-gray-50"
                >
                    <ArrowLeft size={22} />
                </button>
                <h1 className="text-base font-bold text-gray-600 tracking-wide ">Shopping Cart ({cartCount} items)</h1>
            </div>

            {/* Cart Items - No horizontal padding, 10px vertical gap */}
            <div className="flex-grow overflow-y-auto py-[10px]">
                <div className="space-y-2.5">
                    {cartApiLoading ?
                        [...Array(4)].map((_, index) => (
                            <CartItemSkeleton key={index} />
                        )) :
                        cartItems.map((item) => (
                            <CartItem
                                key={item.id}
                                item={item}
                                onUpdateQuantity={handleUpdateQuantity}
                                onRemove={handleRemoveItem}
                            />
                        ))


                    }
                </div>
            </div>

            <div className="fixed bottom-0 left-0 w-full  bg-white border-t border-gray-200 z-50 md:hidden pb-[env(safe-area-inset-bottom)]">
                <CheckoutSubtotalNav handleCheckOut={handleCheckOut} />
            </div>
        </div>
    );
};

export default Cart;




const CheckoutSubtotalNav = ({ handleCheckOut }) => {
    const { cartCount, cartItems, getCartTotal } = useAppStore();


    // Calculate actual subtotal from cart items
    const subtotal = getCartTotal();

    return (
        <div className="flex-shrink-0 bg-white border-t border-gray-100 px-4 py-3 z-20">
            {/* Subtotal Row */}
            <div className="flex justify-between items-center mb-2">
                <span className="text-[12px] text-gray-600">
                    Subtotal • <span className="text-gray-400">{cartCount} items</span>
                </span>
                <span className="text-[18px] font-semibold text-gray-900">
                    ৳{subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
            </div>

            {/* Checkout Button with inline security */}
            <button
                onClick={handleCheckOut}
                className="
                            w-full bg-gray-900 hover:bg-black 
                            text-white font-medium text-[13px]
                            py-2.5 rounded-xl
                            flex flex-col items-center justify-center
                            transition-all duration-200 active:scale-[0.97]
                            disabled:opacity-70 disabled:cursor-not-allowed shadow-sm
                        "
            >
                <div className="flex items-center gap-1.5">
                    <>
                        <LockIcon size={14} />
                        <span>Checkout</span>
                    </>
                </div>

                <span className="flex items-center gap-1 mt-1 text-[10px] text-gray-300">
                    <ShieldCheck size={10} className="text-green-400" />
                    Secure Payment
                </span>

            </button>
        </div>
    );
};